import { WeekDay } from '@angular/common';
import {
  Component,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';

import { MatDialogRef } from '@angular/material/dialog';
import { TranslationService } from '@lib/translation/services';

import { range } from 'lodash';

export interface ITimeIntervalModel {
  dateObj: Date;
  formatted: string;
  selected: boolean;
}

export interface IWeekdayIntervalModel extends ITimeIntervalModel {
  selected: boolean;
  weekdayName: string;
  weekdayIndex: number;
}

export interface IWeekdayModel {
  name: string;
  intervals: IWeekdayIntervalModel[];
}

export interface IEditingModel {
  start: IWeekdayIntervalModel;
  end: IWeekdayIntervalModel;
}

@Component({
  selector: 'sn-weekly-scheduler',
  templateUrl: './weekly-scheduler.component.html',
  styleUrls: ['./weekly-scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnWeeklySchedulerComponent {
  @Input()
  set schedules(value: string[]) {
    if (!value) {
      return;
    }
    this._parseSchedules(value);
  }

  @Output()
  public readonly saveClick = new EventEmitter<string[]>();

  public halfHourIntervals: ITimeIntervalModel[];
  public weekdays: IWeekdayModel[] = [];
  public halfHourHeaders: string[] = [];
  public editing: IEditingModel;
  public cronExpressions: string[] = [];
  public isDrawing: boolean;
  public drawingSelect: boolean;
  public marked = false;

  constructor(
    private readonly _matDialogRef: MatDialogRef<SnWeeklySchedulerComponent>,
    private readonly _translationService: TranslationService
  ) {
    this.halfHourIntervals = this.createHalfHourIntervals().map(this.formatDateHHcolonMM);
    // map will create a new array of formatted intervals from the array returned by createHalfHourIntervals

    this.halfHourHeaders = this.halfHourIntervals
      .filter((x) => x.dateObj.getMinutes() === 0)
      .map((x) => x.formatted);

    const weekdayNames = Object.keys(WeekDay).filter((k) => typeof WeekDay[k as any] === 'number');
    weekdayNames.forEach((wdName, wdIndex) => {
      const weekdayRow: IWeekdayModel = { name: this._translateWeekday(wdName), intervals: [] };
      this.halfHourIntervals.forEach((hhi) => {
        const interval = { ...hhi } as IWeekdayIntervalModel;
        interval.weekdayName = this._translateWeekday(wdName);
        interval.weekdayIndex = wdIndex;

        weekdayRow.intervals.push(interval);
      });
      this.weekdays.push(weekdayRow);
    });
  }

  private _translateWeekday(weekdayKey: string): string {
    return this._translationService.translateWeekday(weekdayKey);
  }

  @HostListener('mouseup')
  public stopDrawing(): void {
    if (this.isDrawing) {
      this.isDrawing = false;
    }
  }

  public toggleVisibility(e: { target: { checked: boolean } }, day: IWeekdayModel): void {
    this.marked = e.target.checked;

    if (!this.marked) {
      day.intervals.forEach((interval) => (interval.selected = false));
    }
    if (this.marked) {
      day.intervals.forEach((interval) => (interval.selected = true));
    }
  }

  public weekdayhasCheck(day: IWeekdayModel): boolean {
    return day.intervals.some((interval) => interval.selected);
  }

  public selectInterval(weekday: IWeekdayModel, interval: IWeekdayIntervalModel): void {
    interval.selected = this.drawingSelect;
  }

  public save(): void {
    let start: IWeekdayIntervalModel;
    let end: IWeekdayIntervalModel;

    this.cronExpressions = [];
    this.weekdays.forEach((wd) => {
      wd.intervals.forEach((wdi) => {
        if (wdi.selected) {
          if (start) {
            end = wdi;
          } else {
            start = wdi;
          }
        } else {
          if (start) {
            this.setCron(start, end);
            start = undefined;
            end = undefined;
          }
        }
      });
      if (start) {
        this.setCron(start, end);
      }
      start = undefined;
      end = undefined;
    });
    this.saveClick.emit(this.cronExpressions);
  }

  public setCron(start: IWeekdayIntervalModel, end: IWeekdayIntervalModel): void {
    const startMinute = start.dateObj.getMinutes();
    const startHour = start.dateObj.getHours();
    const startWDIndex = start.weekdayIndex;
    if (end) {
      const endMinute = end.dateObj.getMinutes();
      const endHour = end.dateObj.getHours();

      const hourDifference = endHour - startHour;
      if (hourDifference === 0) {
        this.cronExpressions.push(`* ${startHour} * * ${startWDIndex}`);
      } else if (hourDifference === 1) {
        if (startMinute === 30) {
          this.cronExpressions.push(`30-59 ${startHour} * * ${startWDIndex}`);
          if (endMinute === 30) {
            this.cronExpressions.push(`* ${endHour} * * ${startWDIndex}`);
          } else {
            this.cronExpressions.push(`0-30 ${endHour} * * ${startWDIndex}`);
          }
        } else {
          if (endMinute === 30) {
            this.cronExpressions.push(`* ${startHour}-${endHour} * * ${startWDIndex}`);
          } else {
            this.cronExpressions.push(`* ${startHour} * * ${startWDIndex}`);
            this.cronExpressions.push(`0-30 ${endHour} * * ${startWDIndex}`);
          }
        }
      } else if (hourDifference > 1) {
        if (hourDifference === 23 && startMinute === 0 && endMinute === 30) {
          this.cronExpressions.push(`* * * * ${startWDIndex}`);
        } else {
          if (startMinute === 30) {
            this.cronExpressions.push(`30-59 ${startHour} * * ${startWDIndex}`);
            if (endMinute === 30) {
              this.cronExpressions.push(`* ${startHour + 1}-${endHour} * * ${startWDIndex}`);
            } else {
              const cronHour =
                startHour + 1 === endHour - 1 ? startHour + 1 : `${startHour + 1}-${endHour - 1}`;
              this.cronExpressions.push(`* ${cronHour} * * ${startWDIndex}`);
              this.cronExpressions.push(`0-30 ${endHour} * * ${startWDIndex}`);
            }
          } else {
            if (endMinute === 30) {
              this.cronExpressions.push(`* ${startHour}-${endHour} * * ${startWDIndex}`);
            } else {
              this.cronExpressions.push(`* ${startHour}-${endHour - 1} * * ${startWDIndex}`);
              this.cronExpressions.push(`0-30 ${endHour} * * ${startWDIndex}`);
            }
          }
        }
      }
    } else {
      const cronMinute = startMinute === 30 ? '30-59' : '0-30';
      this.cronExpressions.push(`${cronMinute} ${startHour} * * ${startWDIndex}`);
    }
  }

  public formatDateHHcolonMM(date: Date): ITimeIntervalModel {
    // funny name but specific
    const formattedValue =
      date.getMinutes() === 0
        ? date.toLocaleString('en-US', { hour: 'numeric', hour12: true })
        : '';
    return { dateObj: date, formatted: formattedValue, selected: false };
  }

  public createHalfHourIntervals(): Date[] {
    const arbitraryDate = '01/01/2001';

    const from = new Date(arbitraryDate);
    from.setHours(0, 0, 0, 0);
    const fromMilisecs = Date.parse(from.toUTCString());

    const until = new Date(arbitraryDate);
    until.setHours(23, 59, 59, 999);
    const untilMilisecs = Date.parse(until.toUTCString());

    const intervalLength = 30;
    const intervalsPerHour = 60 / intervalLength;
    const milisecsPerHour = 60 * 60 * 1000;
    const max = (Math.abs(untilMilisecs - fromMilisecs) / milisecsPerHour) * intervalsPerHour;

    const time = new Date(fromMilisecs);
    const intervals = []; // more clear name than hours
    for (let i = 0; i <= max; i++) {
      const iTime = new Date(time);
      intervals.push(iTime);
      time.setMinutes(time.getMinutes() + intervalLength);
    }

    return intervals;
  }

  public startDrawing(
    event: MouseEvent,
    weekday: IWeekdayModel,
    interval: IWeekdayIntervalModel,
    selected: boolean,
  ): void {
    event.preventDefault();
    this.isDrawing = true;
    this.drawingSelect = selected;
    this.selectInterval(weekday, interval);
  }

  public drawSelect(weekday: IWeekdayModel, interval: IWeekdayIntervalModel): void {
    if (this.isDrawing) {
      this.selectInterval(weekday, interval);
    }
  }

  public cancel(): void {
    this._matDialogRef.close();
  }

  private _parseSchedules(schedules: string[]): void {
    schedules.forEach((schedule) => {
      const split = schedule.split(' ');
      const daysOfWeek = this._getDays(split[4]);
      const hoursOfDay = this._getHours(split[1]);
      const minutesOfDay = this._getMinutes(split[0]);
      this.weekdays.forEach((weekday) => {
        if (daysOfWeek.indexOf(weekday.intervals[0].weekdayIndex) > -1) {
          weekday.intervals.forEach((interval) => {
            const hour = interval.dateObj.getHours();
            if (hoursOfDay.indexOf(hour) > -1) {
              const minute = interval.dateObj.getMinutes();
              if (minutesOfDay.indexOf(minute) > -1) {
                interval.selected = true;
              }
            }
          });
        }
      });
    });
  }

  private _getDays(dayString: string): number[] {
    const days = [0, 1, 2, 3, 4, 5, 6];
    if (dayString === '*') {
      return days;
    }
    const sDays = dayString.split('-');
    const bottom = parseInt(sDays[0], 10);
    const top = parseInt(sDays[1], 10) || bottom;
    if (bottom === top) {
      return [bottom];
    }
    return range(bottom, top + 1);
  }

  private _getMinutes(minuteString: string): number[] {
    if (minuteString === '*') {
      return [0, 30];
    }
    return [parseInt(minuteString.substr(0, 2), 10)];
  }

  private _getHours(hourString: string): number[] {
    if (hourString === '*') {
      return range(0, 24);
    }
    const hours = hourString.split('-');
    const bottom = parseInt(hours[0], 10);
    const top = parseInt(hours[1], 10) || bottom;
    if (bottom === top) {
      return [bottom];
    }
    return range(bottom, top + 1);
  }
}
